import axios from 'axios';

/**
 * REST service for the user's OWN Immersive API keys (uuid + secret) used to
 * authenticate directly to the Immersive WebSocket. This is NOT the venue
 * exchange-credentials flow (OKX/Binance/Bullish) in account-management, which
 * is saved over the WS via `save_credentials`.
 *
 * All calls hit the auth server over REST with `Authorization: Bearer <token>`,
 * mirroring the pattern established in providers/auth/auth-service.tsx.
 *
 * These functions are intentionally pure (accessToken is passed in) so they are
 * trivially unit-testable with a mocked axios. The zero/short-arg signatures the
 * UI consumes (listApiKeys(), createApiKey(name, permissions), ...) live in the
 * companion hook use-api-keys.tsx, which binds accessToken from AuthContext.
 */

const BASE_URL = import.meta.env.REACT_APP_AUTH_DOMAIN ?? 'https://auth.test.immersive-finance.com';

export interface ApiKey {
  created_at: string;
  uuid: string;
  name: string;
  public_key: string;
  permissions: string[];
  type: string;
}

/** Create returns the secret exactly once; the list endpoint never does. */
export interface ApiKeyWithSecret extends ApiKey {
  secret: string;
}

export interface ApiKeySession {
  created_at: string;
  expires_at: string;
  api_key_uuid: string;
}

/**
 * Any of the four endpoints can return 403 {"requires_consent":[...]} — an
 * attestation gate, distinct from a genuine permissions failure. The app has no
 * consent flow wired yet (grep for `requires_consent` -> 0 hits), so we surface
 * this as a typed error and let the caller decide. TODO(IM-2168): once the
 * app-wide attestation flow exists, route this into it instead of messaging.
 */
export class ApiKeyConsentRequiredError extends Error {
  requiresConsent: string[];

  constructor(requiresConsent: string[]) {
    super('Consent required');
    this.name = 'ApiKeyConsentRequiredError';
    this.requiresConsent = requiresConsent;
  }
}

/** A plain 403 {"detail":"Invalid permissions"} — a genuine perms failure. */
export class ApiKeyPermissionError extends Error {
  constructor(message = 'You do not have permission to perform this action') {
    super(message);
    this.name = 'ApiKeyPermissionError';
  }
}

const authHeaders = (accessToken: string) => ({
  Authorization: `Bearer ${accessToken}`,
});

/**
 * Translate a 403 into the right typed error. A body carrying `requires_consent`
 * is the attestation gate; anything else 403 is a real permissions failure.
 * Non-403 errors are rethrown untouched.
 */
const rethrowGated = (error: unknown): never => {
  // Anything that is not an axios error (network layer aside) is not ours to
  // reinterpret - rethrow untouched.
  if (!axios.isAxiosError(error)) {
    throw error;
  }

  const response = error.response;
  if (response?.status === 403) {
    const data = response.data as { requires_consent?: string[]; detail?: string } | undefined;
    const requiresConsent = data?.requires_consent;
    if (Array.isArray(requiresConsent)) {
      throw new ApiKeyConsentRequiredError(requiresConsent);
    }
    throw new ApiKeyPermissionError(data?.detail ?? undefined);
  }

  throw error;
};

export const listApiKeys = async (accessToken: string): Promise<ApiKey[]> => {
  try {
    const response = await axios.get<ApiKey[]>(`${BASE_URL}/private/api_keys`, {
      headers: authHeaders(accessToken),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    return rethrowGated(error);
  }
};

export const createApiKey = async (
  accessToken: string,
  name: string,
  permissions: string[],
): Promise<ApiKeyWithSecret> => {
  try {
    const response = await axios.post<ApiKeyWithSecret>(
      `${BASE_URL}/private/api_key`,
      { name, permissions },
      { headers: authHeaders(accessToken) },
    );
    return response.data;
  } catch (error) {
    return rethrowGated(error);
  }
};

export const deleteApiKey = async (accessToken: string, uuid: string): Promise<void> => {
  try {
    await axios.delete(`${BASE_URL}/private/api_key/${uuid}`, {
      headers: authHeaders(accessToken),
    });
  } catch (error) {
    rethrowGated(error);
  }
};

export const getApiKeySessions = async (
  accessToken: string,
  uuid: string,
): Promise<ApiKeySession[]> => {
  try {
    const response = await axios.get<{ sessions?: ApiKeySession[] } | ApiKeySession[]>(
      `${BASE_URL}/private/api_key/${uuid}/sessions`,
      { headers: authHeaders(accessToken) },
    );
    // Defensive: backend wrapper shape still being confirmed. Accept either
    // {sessions:[...]} or a bare array.
    const data = response.data;
    if (Array.isArray(data)) {
      return data;
    }
    return Array.isArray(data?.sessions) ? data.sessions : [];
  } catch (error) {
    return rethrowGated(error);
  }
};
